class ProductRecommendations extends HTMLElement {
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.target !== this || mutation.type !== 'attributes') continue;
      if (mutation.attributeName === 'data-error') continue;
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;
      this.#loadRecommendations();
      break;
    }
  });

  connectedCallback() {
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  #currentModel() {
    const fromData = (this.dataset.productModel || '').trim().toLowerCase();
    if (fromData) return fromData;
    const t = (document.title || '').trim();
    return t ? t.split(/\s+/)[0].toLowerCase() : '';
  }

  #itemModel(item) {
    const txt = (item.textContent || '').trim();
    if (!txt) return '';
    return txt.split(/\s+/)[0].toLowerCase();
  }

  #handleOf(item) {
    const a = item.querySelector('a[href*="/products/"]');
    if (!a) return null;
    const m = (a.getAttribute('href') || '').match(/\/products\/([^?\/#]+)/);
    return m ? m[1] : null;
  }

  async #fetchOnce(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;
    try {
      const r = await fetch(url, { cache: 'no-store' });
      if (!r.ok) return null;
      return await r.text();
    } catch (e) {
      return null;
    }
  }

  // Collect unique products (excluding the current model) from several random
  // sets, then place them 1:1 into the existing card slots so the layout and
  // the randomizer stay intact — always full, never the same model.
  async #buildRecommendations(productId, sectionId, intent) {
    const current = this.#currentModel();
    const chosen = new Map();
    let templateHtml = null;
    let wanted = 0;

    for (let i = 0; i < 12; i++) {
      if (wanted && chosen.size >= wanted) break;
      const data = await this.#fetchOnce(productId, sectionId, intent);
      if (!data) continue;
      const wrap = document.createElement('div');
      wrap.innerHTML = data;
      const recEl = wrap.querySelector(`product-recommendations[id="${this.id}"]`);
      if (!recEl) continue;

      if (!templateHtml) {
        templateHtml = recEl.innerHTML;
        const g = recEl.querySelector('.resource-list--grid') || recEl.querySelector('.resource-list');
        wanted = g ? g.querySelectorAll('.resource-list__item').length : 0;
        if (!wanted) wanted = 4;
      }

      const src = recEl.querySelector('.resource-list--grid') || recEl;
      src.querySelectorAll('.resource-list__item').forEach((it) => {
        const handle = this.#handleOf(it);
        if (!handle || chosen.has(handle)) return;
        if (current && this.#itemModel(it) === current) return;
        chosen.set(handle, it.cloneNode(true));
      });
    }

    if (!templateHtml || chosen.size === 0) return null;
    return this.#assemble([...chosen.values()], templateHtml, wanted || 4);
  }

  #assemble(items, templateHtml, wanted) {
    const pool = items.slice(0, wanted);
    const wrap = document.createElement('div');
    wrap.innerHTML = templateHtml;
    const lists = wrap.querySelectorAll('.resource-list');
    lists.forEach((list) => {
      const slots = [...list.querySelectorAll('.resource-list__item')];
      slots.forEach((slot, idx) => {
        const repl = pool[idx % pool.length];
        if (repl) {
          slot.replaceWith(repl.cloneNode(true));
        } else {
          slot.remove();
        }
      });
    });
    return wrap.innerHTML;
  }

  async #loadRecommendations() {
    const { productId, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    try {
      const html = await this.#buildRecommendations(productId, sectionId, intent);
      if (html && html.trim().length) {
        this.dataset.recommendationsPerformed = 'true';
        this.innerHTML = html;
      } else if (!window.Shopify || !Shopify.designMode) {
        this.#handleError(new Error('No recommendations available'));
      }
    } catch (e) {
      this.#handleError(e);
    }
  }

  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
